/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "How to migrate to Hightouch Events", description = "Learn how to migrate to Hightouch Events from another event collection solution.", lastUpdatedDate = new Date(1729036800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "How to migrate to Hightouch Events",
        description: "Learn how to migrate to Hightouch Events from another event collection solution.",
        lastUpdatedDate: "Wed Oct 16 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What you'll learn",
          "depth": 0,
          "id": "what-youll-learn"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The key to an effective and on-time migration between two event tracking systems is thorough planning and detail-oriented execution."
      }), "\n", _jsx(_components.p, {
        children: "This guide provides a roadmap for migrating from your existing event tracking solution (such as Segment or Rudderstack) to Hightouch Events based on our experience helping customers migrate. Following this roadmap can minimize disruption to your existing data workflows while ensuring a smooth transition."
      }), "\n", _jsx(_components.h2, {
        id: "what-youll-learn",
        children: "What you'll learn"
      }), "\n", _jsx(_components.p, {
        children: "In this guide, we'll cover:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "preparing-for-migration",
              children: "Preparing for migration"
            })
          }), ": Scope your migration, decide on your data model and tracking plan, and estimate the time and resources required."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "implementing-hightouch-events",
              children: "Implementing Hightouch Events SDKs"
            })
          }), ": Switch to Hightouch SDKs with an analytics wrapper function to support validation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "defining-data-contracts",
              children: "Defining data contracts for data governance"
            })
          }), ": Establish data contracts early in the process to ensure data quality and consistency."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "validating-your-migration",
              children: "Validating your migration"
            })
          }), ": Verify your setup and ensure data quality matches or exceeds your previous platform."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "unifying-data",
              children: "Unifying historical and new data"
            })
          }), ": Deduplicate and unify your historical data with new events collected by Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "setting-up-alerts",
              children: "Setting up monitoring and alerts"
            })
          }), ": Set up monitoring, observability, and advanced alerting for long-term success."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "updating-downstream-applications",
              children: "Updating downstream applications and destinations"
            })
          }), ": Configure or set up the applications that consume your event data."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "By following this guide, you can leverage Hightouch Events' warehouse-centric architecture, flexible schema options, and data governance features while maintaining continuity with your existing data processes."
      })]
    });
  }
}
export default MDXContent;
